import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import useSSE from '../hooks/useSSE';

const Navbar = () => {
  const { fleetManager, swapStationManager, admin, dataRecords, isReadonly, canManageFleet, hasPermission, logout, linkedRoles, switchAccount, loading: authLoading } = useAuth();
  const [pendingForms, setPendingForms] = useState(0);
  const [openTickets, setOpenTickets] = useState(0);
  const [myTicketNotifs, setMyTicketNotifs] = useState(0);
  const [pendingDeletionRequests, setPendingDeletionRequests] = useState(0);
  const [navOpen, setNavOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const switcherRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!admin && !swapStationManager && !dataRecords && !fleetManager) return;
    fetchPendingForms();
    if (admin) { fetchOpenTickets(); fetchDeletionRequestCount(); }
    if (fleetManager) fetchMyTicketNotifs();
    const interval = setInterval(() => {
      fetchPendingForms();
      if (admin) { fetchOpenTickets(); fetchDeletionRequestCount(); }
      if (fleetManager) fetchMyTicketNotifs();
    }, 300000);
    return () => clearInterval(interval);
  }, [admin, swapStationManager, dataRecords, fleetManager]);

  useSSE('bookings:changed', useCallback(() => {
    fetchPendingForms();
  }, []));

  useSSE('forms:changed', useCallback(() => fetchPendingForms(), []));

  const fetchOpenTickets = async () => {
    try {
      const res = await axios.get('/api/support-tickets/admin/count');
      setOpenTickets(res.data.count || 0);
    } catch (e) {}
  };

  const fetchMyTicketNotifs = async () => {
    try {
      const res = await axios.get('/api/support-tickets/my-notifications');
      setMyTicketNotifs(res.data.count || 0);
    } catch (e) {}
  };

  const fetchDeletionRequestCount = async () => {
    try {
      const res = await axios.get('/api/user-admin/booking-deletion-requests');
      const pending = (res.data.requests || []).filter(r => r.status === 'pending');
      setPendingDeletionRequests(pending.length);
    } catch (e) {}
  };

  const fetchPendingForms = async () => {
    if (!swapStationManager && !dataRecords) return;
    try {
      const res = await axios.get('/api/bookings/pending-forms/count');
      setPendingForms(res.data.count || 0);
    } catch (e) {
      // console.error('Failed to fetch pending forms');
    }
  };

  const handleLogout = () => {
    logout();
    setNavOpen(false);
    navigate('/login');
  };

  const closeNav = () => setNavOpen(false);

  const ROLE_DASHBOARDS = {
    fleet_manager: '/',
    swap_station_manager: '/admin',
    admin: '/user-admin',
    data_records: '/data-records',
    customer_admin: '/customer-admin',
  };

  const handleSwitchAccount = async (role, accountId) => {
    setSwitching(true);
    const result = await switchAccount(role, accountId);
    setSwitching(false);
    setSwitcherOpen(false);
    if (result.success) {
      navigate(ROLE_DASHBOARDS[result.userType] || '/');
    } else {
      alert(result.message || 'Failed to switch account');
    }
  };

  const currentUserLabel = fleetManager
    ? `${fleetManager.username} (Fleet)`
    : swapStationManager
    ? `${swapStationManager.username} (Station)`
    : admin
    ? `${admin.username} (Admin)`
    : dataRecords
    ? `${dataRecords.username} (Data)`
    : null;

  const isLoggedIn = fleetManager || swapStationManager || admin || dataRecords;
  const showSwitcher = isLoggedIn && linkedRoles && linkedRoles.length > 0;

  useEffect(() => {
    if (!switcherOpen) return;
    const handleClickOutside = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) setSwitcherOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [switcherOpen]);

  const BADGE_MAP = {
    fleet_manager: 'bg-info',
    swap_station_manager: 'bg-warning text-dark',
    admin: 'bg-dark',
    data_records: 'bg-success',
    customer_admin: 'bg-primary',
  };

  const accountDisplayName = (acct, role) => {
    if (role === 'fleet_manager') {
      return acct.company_name ? `${acct.username} (${acct.company_name})` : acct.username;
    }
    if (role === 'swap_station_manager') {
      return acct.swap_station_name ? `${acct.username} — ${acct.swap_station_name}` : acct.username;
    }
    if (role === 'customer_admin') {
      return acct.company_name ? `${acct.username} (${acct.company_name})` : acct.username;
    }
    return acct.username || acct.email;
  };

  const renderSwitcher = () => {
    if (!showSwitcher) return null;
    return (
      <li className="position-relative" ref={switcherRef}>
        <button
          className="btn btn-outline-light btn-sm"
          onClick={() => setSwitcherOpen(!switcherOpen)}
          disabled={switching}
          style={{ fontSize: '0.78rem', padding: '4px 10px' }}
        >
          {switching ? '...' : `⇄ ${currentUserLabel}`}
        </button>
        {switcherOpen && (
          <div style={{
            position: 'absolute', right: 0, top: '100%', zIndex: 1050, minWidth: '280px', maxHeight: '450px', overflowY: 'auto',
            background: '#fff', border: '1px solid #dee2e6', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '6px 0'
          }}>
            <div style={{ padding: '6px 14px', fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>Switch Account</div>
            {linkedRoles.map(group => (
              <div key={group.role}>
                <div style={{ padding: '5px 14px 2px', fontSize: '0.68rem', fontWeight: 700, color: '#555', borderTop: '1px solid #eee', marginTop: '2px' }}>
                  <span className={`badge ${BADGE_MAP[group.role] || 'bg-secondary'} me-1`} style={{ fontSize: '0.6rem' }}>{group.badge}</span>
                  <span className="text-muted">({group.accounts.length})</span>
                </div>
                {group.accounts.map(acct => (
                  <button
                    key={`${group.role}-${acct.id}`}
                    style={{
                      fontSize: '0.82rem', padding: '5px 14px 5px 24px', border: 'none', background: 'none',
                      width: '100%', textAlign: 'left', cursor: 'pointer', display: 'block', color: '#333'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                    onClick={() => handleSwitchAccount(group.role, acct.id)}
                  >
                    {accountDisplayName(acct, group.role)}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </li>
    );
  };

  const handleFormsClick = (e) => {
    if (location.pathname === '/admin' && location.search.includes('tab=forms')) {
      const el = document.querySelector('[ref-name="pendingFormsSection"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        const heading = Array.from(document.querySelectorAll('h5')).find(h => h.textContent.includes('Pending Forms'));
        if (heading) {
          heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  const renderReadonlyBadge = () => {
    if (!isReadonly) return null;
    return (
      <li>
        <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem', padding: '4px 8px' }}>
          Read-Only
        </span>
      </li>
    );
  };

  return (
    <nav className="app-navbar">
      <div className="container">
        <div className="d-flex justify-content-between align-items-center" style={{ flexWrap: 'nowrap' }}>
          <Link to="/" className="app-navbar-brand" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/logo.svg" alt="EIM One" className="app-navbar-logo" />
          </Link>

          <button className="navbar-toggler" onClick={() => setNavOpen(prev => !prev)} aria-label="Toggle navigation">
            {navOpen ? '✕' : '☰'}
          </button>

          <ul className={`app-nav${navOpen ? ' nav-open' : ''}`}>
            {fleetManager && (
              <>
                {hasPermission('fm_dashboard') && <li><Link to="/" onClick={closeNav}>Dashboard</Link></li>}
                {hasPermission('fm_trucks') && <li><Link to="/trucks" onClick={closeNav}>My Trucks</Link></li>}
                {hasPermission('fm_live_status') && <li><Link to="/live-status" onClick={closeNav}>Live Status</Link></li>}
                {hasPermission('fm_bookings') && <li><Link to="/book" onClick={closeNav}>Book Slot</Link></li>}
                {hasPermission('fm_bookings') && <li><Link to="/my-bookings" onClick={closeNav}>My Bookings</Link></li>}
                {hasPermission('fm_reports') && <li><Link to="/reports" onClick={closeNav}>Reports</Link></li>}
                {canManageFleet && <li><Link to="/fleet-managers" onClick={closeNav}>Fleet Managers</Link></li>}
                {hasPermission('fm_customer_info') && (
                  <li className="position-relative">
                    <Link to="/communication-directory" onClick={closeNav}>Customer Info</Link>
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem', marginTop: '10px' }}>✦</span>
                  </li>
                )}
                <li><Link to="/faq" onClick={closeNav}>FAQ</Link></li>
                {hasPermission('fm_support') && (
                  <li className="position-relative">
                    <Link to="/support-tickets" onClick={closeNav}>Support</Link>
                    {myTicketNotifs > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem', marginTop: '10px' }}>
                        {myTicketNotifs}
                      </span>
                    )}
                  </li>
                )}
                {renderReadonlyBadge()}
                {renderSwitcher()}
                <li>
                  <button className="btn btn-secondary" onClick={handleLogout}>
                    Logout
                  </button>
                </li>
              </>
            )}

            {swapStationManager && (
              <>
                {swapStationManager.swap_station_name && (
                  <li>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.22)',
                      borderRadius: 20,
                      padding: '3px 10px 3px 7px',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: '#fff',
                      letterSpacing: '0.01em',
                      whiteSpace: 'nowrap',
                    }}>
                      <span style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: '#4ade80',
                        boxShadow: '0 0 6px #4ade80',
                        flexShrink: 0,
                      }} />
                      {swapStationManager.swap_station_name}
                    </span>
                  </li>
                )}
                {hasPermission('ssm_dashboard') && <li><Link to="/admin" onClick={closeNav}>Dashboard</Link></li>}
                {hasPermission('ssm_forms') && (
                  <li className="position-relative">
                    <Link
                      to="/admin?tab=forms"
                      onClick={(e) => { closeNav(); handleFormsClick(e); }}
                      style={{ fontWeight: location.search.includes('tab=forms') ? 'bold' : 'normal' }}
                    >
                      Forms
                    </Link>
                    {pendingForms > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem', marginTop: '10px' }}>
                        {pendingForms}
                      </span>
                    )}
                  </li>
                )}
                {renderReadonlyBadge()}
                {renderSwitcher()}
                <li>
                  <button className="btn btn-secondary" onClick={handleLogout}>
                    Logout
                  </button>
                </li>
              </>
            )}

            {/* While the session is still being restored, show nothing rather
                than flashing the logged-out Login link next to a logged-in page. */}
            {!authLoading && !fleetManager && !swapStationManager && !admin && !dataRecords && (
              <>
                <li><Link to="/login" onClick={closeNav}>Login</Link></li>
              </>
            )}

            {admin && (
              <>
                <li><Link to="/user-admin" onClick={closeNav}>Home</Link></li>
                <li className="position-relative">
                  <Link to="/user-admin/requests" onClick={closeNav}>Requests</Link>
                  {pendingDeletionRequests > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem', marginTop: '10px' }}>
                      {pendingDeletionRequests}
                    </span>
                  )}
                </li>
                {hasPermission('adm_support') && (
                  <li className="position-relative">
                    <Link to="/user-admin/support-tickets" onClick={closeNav}>Support</Link>
                    {openTickets > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem', marginTop: '10px' }}>
                        {openTickets}
                      </span>
                    )}
                  </li>
                )}
                {renderReadonlyBadge()}
                {renderSwitcher()}
                <li>
                  <button className="btn btn-secondary" onClick={handleLogout}>
                    Logout
                  </button>
                </li>
              </>
            )}

            {dataRecords && (
              <>
                {hasPermission('dr_dashboard') && <li><Link to="/data-records" onClick={closeNav}>Data Records</Link></li>}
                {hasPermission('dr_bookings') && <li><Link to="/data-records/bookings" onClick={closeNav}>Bookings</Link></li>}
                {hasPermission('dr_vehicles') && <li><Link to="/data-records/map" onClick={closeNav}>Vehicles</Link></li>}
                {renderReadonlyBadge()}
                {renderSwitcher()}
                <li>
                  <button className="btn btn-secondary" onClick={handleLogout}>
                    Logout
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
